import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { clerkEnabled, getActiveAccountIdentity } from '../auth/clerk';
import { createSimpleTheme, getSimpleTokens, SIMPLE_ACCENTS, SIMPLE_TYPEFACE, simpleExtraCssVariables, type SimpleAccent } from './simpleTheme';
import { fontStylesheetUrl, getTokens, installTokenCssVariables, PALETTES, TYPEFACES, type StromaMode, type StromaPalette, type StromaTokens, type StromaTypeface } from './tokens';

export const APPEARANCE_KEY = 'stroma-appearance';
export const APPEARANCE_PENDING_KEY = 'stroma-appearance-pending';
export const APPEARANCE_ACCOUNT_KEY = 'stroma-appearance-account';
const LEGACY_PALETTE_KEY = 'stroma.palette';
const LEGACY_MODE_KEY = 'stroma.mode';
const LEGACY_TYPE_KEY = 'stroma.typeface';
export const APPEARANCE_EVENT = 'stroma:appearance';
export interface StromaAppearance {
  mode: StromaMode | 'system';
  palette: StromaPalette;
  typeface: StromaTypeface;
}
export const DEFAULT_APPEARANCE: StromaAppearance = { mode: 'system', palette: 'indigo', typeface: 'brico' };
let volatileAppearance: StromaAppearance = DEFAULT_APPEARANCE;
let volatileOverride = false;
const volatileAccountAppearances = new Map<string, StromaAppearance>();

export function normalizeStromaAppearance(value: unknown): StromaAppearance | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<StromaAppearance>;
  if (!['light', 'dark', 'system'].includes(candidate.mode || '')) return null;
  if (!PALETTES.includes(candidate.palette as StromaPalette)) return null;
  if (!candidate.typeface || !Object.prototype.hasOwnProperty.call(TYPEFACES, candidate.typeface)) return null;
  return candidate as StromaAppearance;
}

function storedAppearance(): StromaAppearance | null {
  try {
    const combined = window.localStorage.getItem(APPEARANCE_KEY);
    if (combined) return normalizeStromaAppearance(JSON.parse(combined));
    return getLegacyStromaAppearance();
  } catch {
    return null;
  }
}

function storedAccountAppearance(accountIdentity: string): StromaAppearance | null {
  try {
    return normalizeStromaAppearance(JSON.parse(window.localStorage.getItem(appearanceAccountKey(accountIdentity)) || 'null'));
  } catch {
    return null;
  }
}

export function getLegacyStromaAppearance(): StromaAppearance | null {
  try {
    const mode = window.localStorage.getItem(LEGACY_MODE_KEY);
    const palette = window.localStorage.getItem(LEGACY_PALETTE_KEY);
    const typeface = window.localStorage.getItem(LEGACY_TYPE_KEY);
    if (!mode && !palette && !typeface) return null;
    return normalizeStromaAppearance({
      mode: mode === 'light' || mode === 'dark' ? mode : 'system',
      palette: PALETTES.includes(palette as StromaPalette) ? palette : 'indigo',
      typeface: Object.prototype.hasOwnProperty.call(TYPEFACES, typeface || '') ? typeface : 'brico',
    });
  } catch {
    return null;
  }
}

export function getStromaAppearance(accountIdentity?: string | null): StromaAppearance {
  if (accountIdentity) {
    return volatileAccountAppearances.get(accountIdentity) ?? storedAccountAppearance(accountIdentity) ?? DEFAULT_APPEARANCE;
  }
  return volatileOverride ? volatileAppearance : storedAppearance() ?? volatileAppearance;
}

export function getStromaModePreference(): StromaMode | 'system' {
  return getStromaAppearance().mode;
}

export function getPendingStromaAppearance(accountIdentity: string | null): StromaAppearance | null {
  try {
    return normalizeStromaAppearance(JSON.parse(window.localStorage.getItem(pendingAppearanceKey(accountIdentity)) || 'null'));
  } catch {
    return null;
  }
}

export function markStromaAppearancePending(accountIdentity: string | null, appearance: StromaAppearance): void {
  try {
    window.localStorage.setItem(pendingAppearanceKey(accountIdentity), JSON.stringify(appearance));
  } catch {}
}

export function clearPendingStromaAppearance(accountIdentity: string | null, appearance: StromaAppearance): void {
  const pending = getPendingStromaAppearance(accountIdentity);
  if (!pending || JSON.stringify(pending) !== JSON.stringify(appearance)) return;
  try { window.localStorage.removeItem(pendingAppearanceKey(accountIdentity)); } catch {}
}

function pendingAppearanceKey(accountIdentity: string | null): string {
  return `${APPEARANCE_PENDING_KEY}:${encodeURIComponent(accountIdentity ?? 'local')}`;
}

export function appearanceAccountKey(accountIdentity: string): string {
  return `${APPEARANCE_ACCOUNT_KEY}:${encodeURIComponent(accountIdentity)}`;
}

function systemMode(): StromaMode {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function setStromaAppearance(appearance: StromaAppearance, accountIdentity?: string | null): void {
  const normalized = normalizeStromaAppearance(appearance);
  if (!normalized) return;
  volatileAppearance = normalized;
  if (accountIdentity) volatileAccountAppearances.set(accountIdentity, normalized);
  try {
    window.localStorage.setItem(APPEARANCE_KEY, JSON.stringify(normalized));
    if (accountIdentity) window.localStorage.setItem(appearanceAccountKey(accountIdentity), JSON.stringify(normalized));
    window.localStorage.removeItem(LEGACY_MODE_KEY);
    window.localStorage.removeItem(LEGACY_PALETTE_KEY);
    window.localStorage.removeItem(LEGACY_TYPE_KEY);
    volatileOverride = false;
    if (accountIdentity) volatileAccountAppearances.delete(accountIdentity);
  } catch {
    // The current page can still receive the appearance event when storage is blocked.
    volatileOverride = true;
  }
  applyStromaAppearance(normalized, accountIdentity);
}

export function applyStromaAppearance(appearance: StromaAppearance, accountIdentity?: string | null): void {
  const normalized = normalizeStromaAppearance(appearance);
  if (!normalized) return;
  window.dispatchEvent(new CustomEvent(APPEARANCE_EVENT, { detail: { appearance: normalized, accountIdentity: accountIdentity ?? null } }));
}

export function setStromaMode(mode: StromaMode | 'system'): void {
  setStromaAppearance({ ...getStromaAppearance(), mode });
}

/** The simplified app's appearance (#1502): its own key, never today's keys or the server preference. */
export const SIMPLE_APPEARANCE_KEY = 'stroma-simple-appearance';
export const SIMPLE_APPEARANCE_EVENT = 'stroma:simple-appearance';
export type SimpleThemeMode = StromaMode | 'system';
type SimpleChoice = { mode?: SimpleThemeMode; accent?: SimpleAccent };
const SimpleModeContext = React.createContext<SimpleThemeMode>('system');
const SimpleAccentContext = React.createContext<SimpleAccent>('teal');
const volatileSimpleChoices = new Map<string, SimpleChoice>();

export function simpleAppearanceKey(accountIdentity: string | null): string {
  return `${SIMPLE_APPEARANCE_KEY}:${encodeURIComponent(accountIdentity ?? 'local')}`;
}

function currentAccountIdentity(): string | null {
  return clerkEnabled() ? getActiveAccountIdentity() : null;
}

function isSimpleMode(value: unknown): value is SimpleThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

function isSimpleAccent(value: unknown): value is SimpleAccent {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(SIMPLE_ACCENTS, value);
}

/** The simplified app's stored choices for this account: only recognised values, nothing else. */
function getSimpleChoice(accountIdentity: string | null): SimpleChoice {
  const key = simpleAppearanceKey(accountIdentity);
  const volatile = volatileSimpleChoices.get(key);
  if (volatile) return volatile;
  try {
    const stored = JSON.parse(window.localStorage.getItem(key) || 'null');
    return {
      ...(isSimpleMode(stored?.mode) ? { mode: stored.mode } : {}),
      ...(isSimpleAccent(stored?.accent) ? { accent: stored.accent } : {}),
    };
  } catch {}
  return {};
}

function setSimpleChoice(update: SimpleChoice, accountIdentity: string | null): void {
  const key = simpleAppearanceKey(accountIdentity);
  const next = { ...getSimpleChoice(accountIdentity), ...update };
  try {
    window.localStorage.setItem(key, JSON.stringify(next));
    volatileSimpleChoices.delete(key);
  } catch {
    volatileSimpleChoices.set(key, next);
  }
  window.dispatchEvent(new CustomEvent(SIMPLE_APPEARANCE_EVENT, { detail: { accountIdentity } }));
}

/** The simplified app's chosen theme for this account, or null when none was chosen. */
export function getSimpleMode(accountIdentity: string | null): SimpleThemeMode | null {
  return getSimpleChoice(accountIdentity).mode ?? null;
}

export function setSimpleMode(mode: SimpleThemeMode, accountIdentity: string | null): void {
  if (!isSimpleMode(mode)) return;
  setSimpleChoice({ mode }, accountIdentity);
}

/** The simplified app's chosen accent for this account (#1513); teal when none was chosen. */
export function getSimpleAccent(accountIdentity: string | null): SimpleAccent {
  return getSimpleChoice(accountIdentity).accent ?? 'teal';
}

export function setSimpleAccent(accent: SimpleAccent, accountIdentity: string | null): void {
  if (!isSimpleAccent(accent)) return;
  setSimpleChoice({ accent }, accountIdentity);
}

export function useSimpleAccent(): SimpleAccent {
  return React.useContext(SimpleAccentContext);
}

/** The theme the simplified app shows: its own choice, else today's stored mode (read, never written). */
export function useSimpleMode(): SimpleThemeMode {
  return React.useContext(SimpleModeContext);
}

const StromaTokensContext = React.createContext<{ tokens: StromaTokens; typeface: StromaTypeface }>({ tokens: getTokens(), typeface: 'brico' });

/** The tokens and typeface the page is drawn with right now: the simplified app's look (#1502).
 *  Code that builds colours outside MUI (charts) reads these. */
export function useStromaTokens(): { tokens: StromaTokens; typeface: StromaTypeface } {
  return React.useContext(StromaTokensContext);
}

/**
 * The app's theme (#1502): the simplified app's look. Its theme is `setSimpleMode`'s choice, else today's stored
 * mode (read, never written); its accent is `setSimpleAccent`'s, else teal.
 */
export default function StromaThemeProvider({ children }: React.PropsWithChildren) {
  const initialAppearance = clerkEnabled() ? DEFAULT_APPEARANCE : getStromaAppearance();
  const preference = React.useRef<StromaAppearance>(initialAppearance);
  const [appearance, setAppearance] = React.useState(initialAppearance);
  const [simpleChoice, setSimpleChoiceState] = React.useState<SimpleChoice>(() => getSimpleChoice(currentAccountIdentity()));
  const requestedMode = simpleChoice.mode ?? appearance.mode;
  const accent: SimpleAccent = simpleChoice.accent ?? 'teal';
  const simpleFollowsSystem = React.useRef(false);
  simpleFollowsSystem.current = requestedMode === 'system';
  const [, setSystemTick] = React.useState(0);
  const mode = requestedMode === 'system' ? systemMode() : requestedMode;
  const { palette, typeface } = appearance;
  const fontFace = SIMPLE_TYPEFACE;
  const tokens = React.useMemo(() => getSimpleTokens(mode, accent), [mode, accent]);
  const tokensContext = React.useMemo(() => ({ tokens, typeface: fontFace }), [tokens, fontFace]);

  React.useLayoutEffect(() => {
    const reread = () => setSimpleChoiceState(getSimpleChoice(currentAccountIdentity()));
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === simpleAppearanceKey(currentAccountIdentity())) reread();
    };
    reread();
    window.addEventListener(SIMPLE_APPEARANCE_EVENT, reread);
    // an account switch announces itself as an appearance event
    window.addEventListener(APPEARANCE_EVENT, reread);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SIMPLE_APPEARANCE_EVENT, reread);
      window.removeEventListener(APPEARANCE_EVENT, reread);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  React.useLayoutEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    const applyPreference = (requested: StromaAppearance) => {
      preference.current = requested;
      setAppearance(requested);
    };
    const refresh = () => {
      const accountIdentity = clerkEnabled() ? getActiveAccountIdentity() : null;
      const requested = accountIdentity ? getStromaAppearance(accountIdentity) : storedAppearance() ?? DEFAULT_APPEARANCE;
      volatileAppearance = requested;
      volatileOverride = false;
      applyPreference(requested);
    };
    const onStorage = (event: StorageEvent) => {
      const accountIdentity = clerkEnabled() ? getActiveAccountIdentity() : null;
      const expectedKey = accountIdentity ? appearanceAccountKey(accountIdentity) : APPEARANCE_KEY;
      if (event.key === expectedKey || event.key === null) {
        if (accountIdentity) volatileAccountAppearances.delete(accountIdentity);
        refresh();
      }
    };
    const onAppearance = (event: Event) => {
      const detail = (event as CustomEvent<{ appearance?: unknown; accountIdentity?: string | null }>).detail;
      if (clerkEnabled() && detail?.accountIdentity !== getActiveAccountIdentity()) return;
      const requested = normalizeStromaAppearance(detail?.appearance);
      if (requested) applyPreference(requested); else refresh();
    };
    const onSystemChange = () => {
      if (preference.current.mode === 'system') setAppearance({ ...preference.current });
      else if (simpleFollowsSystem.current) setSystemTick((tick) => tick + 1);
    };
    media?.addEventListener?.('change', onSystemChange);
    window.addEventListener('storage', onStorage);
    window.addEventListener(APPEARANCE_EVENT, onAppearance);
    return () => {
      media?.removeEventListener?.('change', onSystemChange);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(APPEARANCE_EVENT, onAppearance);
    };
  }, []);

  React.useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.colorScheme = mode;
    root.dataset.pal = palette;
    root.dataset.mode = mode;
    root.dataset.type = typeface;
    installTokenCssVariables(root, tokens, SIMPLE_TYPEFACE);
    root.dataset.ui = 'simple';
    // the mock sets everything in one Geist stack, headings included
    root.style.setProperty('--display', TYPEFACES[SIMPLE_TYPEFACE].body);
    Object.entries(simpleExtraCssVariables(mode, accent)).forEach(([name, value]) => root.style.setProperty(name, value));
    Object.entries({
      '--primary': tokens.accent,
      '--primary-light': tokens['accent-hi'],
      '--primary-dark': tokens['accent-ink'],
      '--bg': tokens.ground,
      '--text': tokens.ink,
      '--text-secondary': tokens['ink-2'],
      '--border': tokens.line,
      '--success': tokens.ok,
      '--error': tokens.bad,
      '--warning': tokens.warn,
      '--info': tokens.agent,
    }).forEach(([name, value]) => root.style.setProperty(name, value));
  }, [mode, palette, typeface, tokens, accent]);

  React.useEffect(() => {
    const id = 'stroma-product-fonts';
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      document.head.append(link);
    }
    link.href = fontStylesheetUrl([fontFace]);
    return undefined;
  }, [fontFace]);

  const theme = React.useMemo(() => createSimpleTheme(mode, accent), [mode, accent]);

  return (
    <SimpleModeContext.Provider value={requestedMode}>
      <SimpleAccentContext.Provider value={accent}>
        <StromaTokensContext.Provider value={tokensContext}>
          <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </StromaTokensContext.Provider>
      </SimpleAccentContext.Provider>
    </SimpleModeContext.Provider>
  );
}
